import React, { useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { loadAuthApi } from "../utils/firebaseAuth";
import toast from "react-hot-toast";
import { BASE_URL, customPasswordRestErrorMessages } from "../utils/tools";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { LuCheck, LuChevronLeft } from "react-icons/lu";
import { Spinner } from "@jammable/ui-components/Loader";
import { useT } from "@jammable/ui-core/src/i18n";
import {
  AuthError,
  authErrorText,
  authFieldError,
  authInput,
  authInputError,
  authLink,
  authPrimary,
} from "../components/auth/JamAuth";
const ForgotPasswordPage = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const t = useT();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(t("auth.invalidEmail"))
      .required(t("auth.emailRequired")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      loadAuthApi()
        .then(({ auth, fb }) =>
          fb.sendPasswordResetEmail(auth, values.email.trim()),
        )
        .then(() => {
          toast.success(t("auth.forgot.sentToast"));
          setSent(true);
        })
        .catch((error) => {
          console.error("Error sending password reset email: ", error);
          setError(
            authErrorText(t, "forgot", customPasswordRestErrorMessages, error),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const title = "Forgot Password | Jammable";
  const url = `${BASE_URL}/resetpassword`;

  return (
    <div className="tw-flex tw-w-full tw-justify-center tw-bg-white tw-px-5 tw-pb-16 tw-pt-6 tw-text-jam-ink md:tw-pt-16">
      <NextSeo
        title={title}
        canonical={url}
        openGraph={{ title: title, url: url }}
      />
      <div className="tw-flex tw-w-full tw-max-w-[420px] tw-flex-col tw-gap-4">
        <Link
          href="/login"
          aria-label={t("auth.backToLogin")}
          className="tw-flex tw-h-[42px] tw-w-[42px] tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-ink"
        >
          <LuChevronLeft
            className="tw-h-[18px] tw-w-[18px]"
            strokeWidth={2.5}
          />
        </Link>
        <h1 className="jam-display tw-m-0 tw-mt-3 tw-text-[36px] tw-font-extrabold tw-leading-none md:tw-text-[44px]">
          {t("auth.forgot.title")}
        </h1>
        <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted">
          {t("auth.forgot.blurb")}
        </p>
        <form
          className="tw-flex tw-w-full tw-flex-col tw-gap-4"
          onChange={() => {
            setError(null);
          }}
          onSubmit={formik.handleSubmit}
        >
          {error && <AuthError>{error}</AuthError>}
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <input
              placeholder={t("auth.emailPlaceholder")}
              aria-label={t("auth.email")}
              inputMode="email"
              autoComplete="email"
              className={`${authInput} ${formik.touched.email && formik.errors.email ? authInputError : ""}`}
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className={`${authFieldError} tw-px-5`}>
                {formik.errors.email}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className={authPrimary}>
            {loading ? (
              <Spinner className="tw-border-white tw-border-3" size={4} />
            ) : (
              t("auth.forgot.send")
            )}
          </button>
        </form>
        {sent && (
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-[22px] tw-bg-[#EEFFB0] tw-px-4 tw-py-3.5">
            <span className="tw-flex tw-h-[26px] tw-w-[26px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime">
              <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3.5} />
            </span>
            <span className="tw-text-[13px] tw-font-extrabold">
              {t("auth.forgot.sent")}
            </span>
          </div>
        )}
        <div className="tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("auth.forgot.google")}{" "}
          <Link href="/login" className={authLink}>
            {t("auth.continueGoogle")}
          </Link>{" "}
          {t("auth.forgot.googleAfter")}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
